/**
 * The access control of one Data Lake path (S8 Task 8.11) over `azure-storage:hns-get-acl`, with one request id per read
 * — cancelled when the query aborts or the editor unmounts. The key sits under the shared `['azure-storage',
 * connectionId]` prefix, per resource (`… containerScopeKey(ref), storageResourceKey(ref), 'acl'`), so every
 * `azure-storage:mutated` refetches an open editor (a recursive job, a rename, an ACL save in another dialog).
 */
import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { containerScopeKey, storageResourceKey, type HnsPathRef, type StorageHnsAccessControl } from '../../../../../types/azureStorage';
import { cancelStorageRequest, newStorageRequestId, toStorageOperationError } from '../../services/azureStorageApi';
import { STORAGE_QUERY_ROOT } from '../../services/storageListingKeys';
import { hnsGetAcl, unwrapHns } from '../hnsApi';

export function hnsAccessControlQueryKey(connectionId: string, ref: HnsPathRef) {
  return [STORAGE_QUERY_ROOT, connectionId, containerScopeKey(ref), storageResourceKey(ref), 'acl'] as const;
}

export function useHnsAccessControl(args: { connectionId: string; resource: HnsPathRef; enabled?: boolean }) {
  const client = useQueryClient();
  const activeCancel = useRef<(() => Promise<void>) | null>(null);
  const resourceKey = storageResourceKey(args.resource);
  const key = useMemo(() => hnsAccessControlQueryKey(args.connectionId, args.resource), [args.connectionId, resourceKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = useQuery({
    queryKey: key, enabled: args.enabled ?? true, retry: false, staleTime: Infinity, refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<StorageHnsAccessControl> => {
      const requestId = newStorageRequestId();
      let cancelled = false;
      const cancel = async () => { if (!cancelled) { cancelled = true; await cancelStorageRequest(requestId); } };
      activeCancel.current = cancel;
      const onAbort = () => void cancel();
      signal.addEventListener('abort', onAbort);
      try {
        return await unwrapHns(hnsGetAcl({ connectionId: args.connectionId, resource: args.resource, requestId }));
      } finally {
        signal.removeEventListener('abort', onAbort);
        if (activeCancel.current === cancel) activeCancel.current = null;
      }
    },
  });
  useEffect(() => () => { void activeCancel.current?.(); client.removeQueries({ queryKey: key, exact: true }); }, [client, key]);
  return {
    data: query.data,
    isLoading: query.isPending && (args.enabled ?? true),
    isFetching: query.isFetching,
    error: query.error ? toStorageOperationError(query.error) : undefined,
    reload: () => void client.resetQueries({ queryKey: key, exact: true }),
    /** Re-reads without dropping the cached answer (the editor keeps its baseline while it shows what changed). */
    refresh: () => void query.refetch(),
  };
}
